import { getConversationTitle } from "@/api/chat";
import { queryClient } from "@/api/queryClient";
import { clearChatReadyToast } from "@/components/frontend/chat/chatReadyToastRegistry";
import { showChatReadyToast } from "@/components/frontend/chat/showChatReadyToast";
import {
    AssistantRuntimeProvider,
    RuntimeAdapterProvider,
    type ChatModelRunResult,
    type ThreadHistoryAdapter,
    useAui,
    useLocalRuntime,
    useRemoteThreadListRuntime,
} from "@assistant-ui/react";
import {
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
    apiThreadListAdapter,
    DEFAULT_CONVERSATION_TITLE,
} from "../adapters/api-thread-list-adapter";
import {
    createChatModelAdapter,
    type AttachTarget,
    type ChatIdentitySnapshot,
} from "../adapters/chat-model-adapter";
import { getAuthSessionEpoch } from "@/contexts/auth/AuthContext";
import { useAuth } from "@/contexts/auth/useAuth";
import { OrganizationContext } from "@/contexts/frontend/organization/OrganizationContext";
import {
    AttachContext,
    type AttachContextValue,
} from "../contexts/AttachContext";
import {
    EnrichmentStateContext,
    type EnrichmentStateValue,
} from "../contexts/EnrichmentStateContext";
import { midStreamRecoveryChannel } from "@/hooks/chat/useMidStreamRecoveryReload";
import { useChatRouteThreadId } from "../hooks/useChatRouteThreadId";

import { RefineContext } from "../contexts/RefineContext";
import { RetryContext } from "../contexts/RetryContext";
import { MidStreamRecoveryReloader } from "./MidStreamRecoveryReloader";
import {
    handleChatTurnCompleted,
    loadConversationRepository,
    refreshConversationListsAfterTurn,
} from "../utils/handleChatTurnCompleted";
import {
    computeIsOffChat,
    getActiveSheetThreadId,
    type SetSheetMode,
    type SheetModeCallback,
    SheetModeContext,
    wrapInitializeWithSheetMode,
} from "./runtime-provider.sheet-mode";
import {
    ThreadListLoadFailedContext,
    trackListLoad,
} from "./runtime-provider.list-status";
import {
    fenceThreadFetch,
    ThreadTargetContext,
    type ThreadTargetValue,
} from "./runtime-provider.thread-target";
import { withChatReadyToast } from "./runtime-provider.title-generation";

const OPENED_CHAT_DETAIL_REUSE_MS = 5_000;

function useRuntimeHook(
    chatModelAdapter: ReturnType<typeof createChatModelAdapter>
) {
    return useLocalRuntime(chatModelAdapter);
}

function useIdSet() {
    const [ids, setIds] = useState<ReadonlySet<number>>(() => new Set());
    const add = useCallback((id: number) => {
        setIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);
    const remove = useCallback((id: number) => {
        setIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);
    return [ids, add, remove] as const;
}

function ThreadHistoryProvider({ children }: { children: ReactNode }) {
    const aui = useAui();

    const history = useMemo<ThreadHistoryAdapter>(
        () => ({
            async load() {
                const { remoteId } = aui.threadListItem().getState();
                if (!remoteId) return { messages: [] };

                return loadConversationRepository(parseInt(remoteId, 10), {
                    reuseDetailFor: OPENED_CHAT_DETAIL_REUSE_MS,
                });
            },

            async append() {},
        }),
        [aui]
    );

    const adapters = useMemo(() => ({ history }), [history]);

    return (
        <RuntimeAdapterProvider adapters={adapters}>
            {children}
        </RuntimeAdapterProvider>
    );
}

export function RuntimeProvider({
    children,
}: Readonly<{ children: ReactNode }>) {
    const location = useLocation();
    const navigate = useNavigate();
    const chatId = useChatRouteThreadId();

    useEffect(() => {
        if (chatId) clearChatReadyToast(chatId);
    }, [chatId]);

    const locationRef = useRef(location);
    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    const navigateRef = useRef(navigate);
    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    const remoteIdRef = useRef<string | undefined>(undefined);

    const refinedFromMessageIdRef = useRef<number | undefined>(undefined);

    const retryTargetMessageIdRef = useRef<number | undefined>(undefined);

    const refineFailureListenersRef = useRef(
        new Set<(messageId: number) => void>()
    );

    const sheetModeRef = useRef<SheetModeCallback | null>(null);
    const setSheetMode = useCallback<SetSheetMode>((callback) => {
        sheetModeRef.current = callback;
    }, []);

    const threadTargetRef = useRef<string | null>(chatId ?? null);
    const [threadTarget, setThreadTarget] = useState<string | null>(
        chatId ?? null
    );
    const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
    const [listFailed, setListFailed] = useState(false);
    const requestThread = useCallback((remoteId: string | null) => {
        threadTargetRef.current = remoteId;
        setThreadTarget(remoteId);
    }, []);
    const threadTargetValue = useMemo<ThreadTargetValue>(
        () => ({ requestThread, target: threadTarget, loadingThreadId }),
        [requestThread, threadTarget, loadingThreadId]
    );

    const routeThreadIdRef = useRef(chatId);
    useEffect(() => {
        if (routeThreadIdRef.current === chatId) return;
        routeThreadIdRef.current = chatId;
        requestThread(chatId ?? null);
    }, [chatId, requestThread]);

    const retryContextValue = useMemo(
        () => ({
            setRetryTargetMessageId: (messageId: number | undefined) => {
                retryTargetMessageIdRef.current = messageId;
            },
        }),
        []
    );

    const refineContextValue = useMemo(
        () => ({
            setRefinedFromMessageId: (messageId: number | undefined) => {
                refinedFromMessageIdRef.current = messageId;
            },
            subscribeRefineFailed: (listener: (messageId: number) => void) => {
                refineFailureListenersRef.current.add(listener);
                return () => {
                    refineFailureListenersRef.current.delete(listener);
                };
            },
        }),
        []
    );

    const [dismissedIds, markDismissed, unmarkDismissed] = useIdSet();
    const [refinedIds, markRefined, unmarkRefined] = useIdSet();

    const enrichmentStateValue = useMemo<EnrichmentStateValue>(
        () => ({
            isDismissed: (id) => dismissedIds.has(id),
            isRefined: (id) => refinedIds.has(id),
            markDismissed,
            unmarkDismissed,
            markRefined,
            unmarkRefined,
        }),
        [
            dismissedIds,
            refinedIds,
            markDismissed,
            unmarkDismissed,
            markRefined,
            unmarkRefined,
        ]
    );

    useEffect(() => {
        return refineContextValue.subscribeRefineFailed(unmarkRefined);
    }, [refineContextValue, unmarkRefined]);

    // Toast gate: suppress when the user is viewing the thread (?threadId=X,
    // /chat/:id path) or has it open in a sheet.
    const isOffChat = (threadId: string) =>
        computeIsOffChat({
            search: locationRef.current.search,
            pathname: locationRef.current.pathname,
            remoteId: threadId,
            activeSheetThreadId: getActiveSheetThreadId(),
        });

    const attachTargetRef = useRef<AttachTarget | undefined>(undefined);

    // The adapter outlives any one render, so the run reads the principal and
    // organization through a ref. The epoch is read from its module instead:
    // logout unmounts this subtree, and a run captured before that still has
    // to see the session end.
    const { user } = useAuth();
    const organizationId =
        useContext(OrganizationContext)?.organization?.id ?? null;
    const principalRef = useRef({
        userId: user?.id ?? null,
        organizationId,
    });
    // Written during render, not in a passive effect: a handoff that resolves
    // between an organization commit and the effect that would have recorded
    // it must not read the organization the user has already left.
    principalRef.current = { userId: user?.id ?? null, organizationId };

    const getIdentitySnapshot = useCallback(
        (): ChatIdentitySnapshot => ({
            ...principalRef.current,
            authSessionEpoch: getAuthSessionEpoch(),
        }),
        []
    );

    const chatModelAdapter = useMemo(
        () =>
            createChatModelAdapter({
                getThreadId: () => remoteIdRef.current,
                getAttachTarget: () => attachTargetRef.current,
                clearAttachTarget: () => {
                    attachTargetRef.current = undefined;
                },
                getRefinedFromMessageId: () => refinedFromMessageIdRef.current,
                clearRefinedFromMessageId: () => {
                    refinedFromMessageIdRef.current = undefined;
                },
                getRetryTargetMessageId: () => retryTargetMessageIdRef.current,
                clearRetryTargetMessageId: () => {
                    retryTargetMessageIdRef.current = undefined;
                },
                onRefineFailed: (messageId: number) => {
                    refineFailureListenersRef.current.forEach((listener) => {
                        try {
                            listener(messageId);
                        } catch (err) {
                            console.error("refine failure listener threw", err);
                        }
                    });
                },
                onMidStreamError: midStreamRecoveryChannel.emit,
                getIdentitySnapshot,
                // The committed copy arrives through the ordinary canonical
                // conversation read, not a second import path.
                onSettledAtRest: midStreamRecoveryChannel.emit,
                onTurnAdmitted: () =>
                    refreshConversationListsAfterTurn(queryClient),
                onComplete: (threadId) => {
                    handleChatTurnCompleted(queryClient);

                    if (!isOffChat(threadId)) return;

                    getConversationTitle(threadId)
                        .then((title) => {
                            if (!title || title === DEFAULT_CONVERSATION_TITLE)
                                return;
                            if (!isOffChat(threadId)) return;
                            showChatReadyToast(
                                title,
                                threadId,
                                navigateRef.current
                            );
                        })
                        .catch(() => {});
                },
            }),
        [getIdentitySnapshot]
    );

    const threadListAdapter = useMemo(
        () => ({
            ...apiThreadListAdapter,
            list: trackListLoad({
                baseList: apiThreadListAdapter.list.bind(apiThreadListAdapter),
                setListFailed,
            }),
            fetch: fenceThreadFetch({
                baseFetch:
                    apiThreadListAdapter.fetch!.bind(apiThreadListAdapter),
                threadTargetRef,
                setLoadingThreadId,
            }),
            // On-page chat surfaces intercept thread-creation navigation via setSheetMode.
            initialize: wrapInitializeWithSheetMode({
                baseInitialize:
                    apiThreadListAdapter.initialize.bind(apiThreadListAdapter),
                navigateRef,
                remoteIdRef,
                sheetModeRef,
            }),
            generateTitle: withChatReadyToast(
                apiThreadListAdapter.generateTitle!,
                isOffChat,
                () => navigateRef.current
            ),
        }),
        []
    );

    const attachContextValue = useMemo<AttachContextValue>(
        () => ({
            armAttachTarget: (target) => {
                attachTargetRef.current = target;
            },
            attachStream: (options) =>
                chatModelAdapter.run(options) as AsyncGenerator<
                    ChatModelRunResult,
                    void,
                    unknown
                >,
        }),
        [chatModelAdapter]
    );

    const runtime = useRemoteThreadListRuntime({
        // assistant-ui calls this as a hook internally despite the callback shape
        // eslint-disable-next-line react-hooks/rules-of-hooks
        runtimeHook: () => useRuntimeHook(chatModelAdapter),
        adapter: {
            ...threadListAdapter,
            unstable_Provider: ThreadHistoryProvider,
        },
        threadId: chatId,
    });

    return (
        <SheetModeContext.Provider value={setSheetMode}>
            <ThreadTargetContext.Provider value={threadTargetValue}>
                <ThreadListLoadFailedContext.Provider value={listFailed}>
                    <RetryContext.Provider value={retryContextValue}>
                        <RefineContext.Provider value={refineContextValue}>
                            <EnrichmentStateContext.Provider
                                value={enrichmentStateValue}
                            >
                                <AttachContext.Provider
                                    value={attachContextValue}
                                >
                                    <AssistantRuntimeProvider runtime={runtime}>
                                        <MidStreamRecoveryReloader
                                            getIdentitySnapshot={
                                                getIdentitySnapshot
                                            }
                                        />
                                        {children}
                                    </AssistantRuntimeProvider>
                                </AttachContext.Provider>
                            </EnrichmentStateContext.Provider>
                        </RefineContext.Provider>
                    </RetryContext.Provider>
                </ThreadListLoadFailedContext.Provider>
            </ThreadTargetContext.Provider>
        </SheetModeContext.Provider>
    );
}
